import { PRODUCTS } from '@xanadu/ui-shared/constants';

import { TakeTimeToThinkBanner } from '../../../common/components/ContentFooter/TopBanners';
import KycCashierRestricted from '../../../common/components/KycCashierRestricted';
import contents from '../../../common/components/Modal/modalConfig';
import { UKRegulators } from '../../../common/components/Regulators';
import Portfolio from '../../../common/pages/Portfolio';
import { getClientDomain, getClientEnabledPhoneNumberPrefixes } from '../../../common/utils';
import { preDefinedAmountsMapper } from '../../../common/utils/bettingModal';
import { featuresFlags, BRAND, customization, routes } from '../../config';
import PREDICTIONS_ACCOUNT_DETAILS_FIELDS from './constants/accountPersonalDetailsFields';
import { useSignUpConfiguration } from './hooks';

const availableRegistrationCurrencies = ['GBP', 'USD', 'EUR'];

// TODO: change to futures (or whatever)
const SITE = 'predictions';

const displayedRegulators = [
  'ALDERNEY',
  'GAMBLING_CONTROL',
  'GAME_CARE',
  'GAMSTOP',
  'TTTT',
  'PLUS18',
  'BET_BLOCKER',
  'GRAI',
];

const DEFAULT_DEPOSIT_LIMIT = {
  EUR: {
    daily: {
      minimum: 10,
      maximum: 10000
    },
    weekly: {
      minimum: 10,
      maximum: 10000
    },
    monthly: {
      minimum: 10,
      maximum: 10000
    }
  },
  GBP: {
    daily: {
      minimum: 10,
      maximum: 10000
    },
    weekly: {
      minimum: 10,
      maximum: 10000
    },
    monthly: {
      minimum: 10,
      maximum: 10000
    }
  },
  USD: {
    daily: {
      minimum: 10,
      maximum: 10000
    },
    weekly: {
      minimum: 10,
      maximum: 10000
    },
    monthly: {
      minimum: 10,
      maximum: 10000
    }
  },
};

const modalContents = contents({
  SignUp: {
    availableRegistrationCurrencies,
    RegLogos: UKRegulators,
    childrenProps: {
      RegLogos: {
        displayedRegulators,
      }
    },
    useConfiguration: useSignUpConfiguration,
  },
  kyc: {
    Component: KycCashierRestricted,
  },
});

const config = {
  site: SITE,
  domain: getClientDomain(BRAND, SITE),
  defaultProduct: PRODUCTS.predictions,
  clientDefaultLanguage: 'en-GB',
  clientDefaultCurrency: 'GBP',
  availableLanguages: ['en-GB'],
  availableCurrencies: availableRegistrationCurrencies,
  availableFeatures: ['exchange', 'cashout', 'price-graphs'],
  excludedSports: ['horse-racing', 'greyhound-racing'],
  availableOddsTypes: ['PROBABILITY'],
  fallbackReleaseDate: new Date('2027-03-31T13:30:00+01:00'),
  homepageBannerPosition: 'top', // [top, bottom, center]
  featuresFlags: {
    ...featuresFlags,
    releaseNotesPosition: 'bottom',
    availableMobileReports: ['positions', 'open-orders', 'settled-trades', 'history'],
    crawlingEnabled: true,
    editActiveOffer: true,
    fixedExchangeType: 'binary',
    fixedOddsType: 'PROBABILITY',
    keepInPlayEnabled: true,
    oddsLockerEnabled: true,
    priceDepth: 1, // maybe better another place as its not a feature
    raceCardsEnabled: false,
    showMatchedVolume: false,
    feedbackEnabled: true,
    showColossus: false,
    enabledTrendingMarkets: true,
    displayAdvancedRaceData: true,
    subProductsEnabled: [
      PRODUCTS.predictions,
      PRODUCTS.exchange,
    ],
    externalProductsEnabled: [
      {
        id: PRODUCTS.exchange,
        name: 'Exchange',
        urlName: 'exchange',
        url: 'https://exchange.easybet.net/',
        external: true,
      },
    ],
  },
  routes: [
    {
      ...routes[0],
      props: {
        childrenProps: {
          EventTime: {
            layout: 'large',
          },
          Event: {
            distantNameLink: true,
            hideCompetitions: true,
          },
        },
      },
    },
    {
      ...routes[1],
      props: {
        childrenProps: {
          Event: {
            distantNameLink: true,
          },
          SingleEventPageMarket: {
            backLayAllEnabled: false,
            showInPlayAndMultipleIcons: false,
          },
          EventsPageHeader: {
            useBreadcrumbs: true,
          },
        },
      },
    },
    ...routes.slice(2),
    {
      path: '/portfolio',
      component: Portfolio,
      props: {
        tabs: [
          {
            key: 'positions',
            label: 'Positions',
            value: 'positions',
            href: '/portfolio/positions',
          },
          {
            key: 'open-orders',
            label: 'Open Orders',
            value: 'open-orders',
            href: '/portfolio/open-orders',
          },
          {
            key: 'settled-trades',
            label: 'Settled Trades',
            value: 'settled-trades',
            href: '/portfolio/settled-trades',
          },
          {
            key: 'history',
            label: 'History',
            value: 'history',
            href: '/portfolio/history',
          },
        ],
      },
    },
  ],
  modalContents,
  enabledPrefixes: getClientEnabledPhoneNumberPrefixes(BRAND, SITE),
  customization: {
    ...customization,
    navigationLayout: 'isolated',
    appSidebarLayout: 'sticky',
    MobileFooter: {
      items: [
        { id: 'home', label: 'Markets' },
        { id: 'menu', label: 'Menu' },
        { id: 'portfolio', label: 'Portfolio' },
        { id: 'promotions', label: 'Promotions' },
        { id: 'account', label: 'Account' },
      ]
    },
    ContentFooter: {
      ...customization.ContentFooter,
      TopFooterBanner: TakeTimeToThinkBanner,
      RegLogos: UKRegulators,
      paymentProvidersList: [
        'visa',
        'mastercard',
        'skrill',
        'neteller',
        'apple',
        'google',
      ],
      childrenProps: {
        RegLogos: {
          displayedRegulators,
        },
      },
    },
    Header: {
      ...customization.Header,
      displaySearch: true,
      displayExtraLinks: false,
      displaySubHeader: true,
      exposureLabel: 'Exposure',
      balanceLabel: 'Cash',
      walletVariant: 'v2',
      forceLongLogo: false,
      childrenProps: {
        SubHeader: {
          b2bLink: 'https://b2b.matchbook.com/',
          brokerageLinkEnabled: false,
          insightLinkEnabled: false,
        },
      }
    },
    Promotions: {
      pageTitle: 'easyBet - Sign Up Offer & Free Bets',
      pageDescription: 'Get the latest offers from easyBet. Bet Yes or No, Opt-in for best rewards. Join Today.',
    },
    ResetPassword: {
      ...customization.ResetPassword,
      RegLogos: UKRegulators,
      childrenProps: {
        RegLogos: {
          displayedRegulators,
        },
      },
    },
    ResponsibleGambling: {
      RegLogos: UKRegulators,
      childrenProps: {
        RegLogos: {
          colored: true,
          displayedRegulators: displayedRegulators.filter((regulator) => regulator !== 'BET_BLOCKER'),
          noText: true,
        },
      },
      displayNetDepositLimitSection: true,
      displayGrossDepositLimitSection: true,
    },
    SingleEventPage: {
      ...customization.SingleEventPage,
      marketsLayout: 'one-per-tab',
      racingInfoLayout: 'toggle',
      streamingPreviewLayout: 'compact',
      eventTimesLayout: 'embedded'
    },
    AffordabilityInfo: {
      DEFAULT_DEPOSIT_LIMIT,
    },
    AccountPagesSettings: {
      displayAccountTitleIcon: false,
      displayAccountTitleUsername: false,
      displayBonusSectionAsInline: false,
      displayCashierButtonsIcons: false,
      displayCashierButtonsMobilePosition: 'bottom',
      displayedDetails: PREDICTIONS_ACCOUNT_DETAILS_FIELDS,
      displayHideDetailsLinkPosition: 'bottom',
      displayLogoutSectionInline: true,
      displayMatchedVolumeSelector: false,
      displayMobileLogOutLinkAs: 'link',
      displayOverroundsSelector: false,
      verificationBadgesAnchor: 'right',
      accountLinks: [
        {
          label: 'Positions',
          value: 'positions',
          href: '/portfolio/positions',
        },
        {
          label: 'Open Orders',
          value: 'open-orders',
          href: '/portfolio/open-orders',
        },
        {
          label: 'Settled Trades',
          value: 'settled-trades',
          href: '/portfolio/settled-trades',
        },
        {
          label: 'History',
          value: 'history',
          href: '/portfolio/history',
        },
      ],
    },
    EventsGroupHeader: {
      variant: 'basic',
    },
    ExchangeBettingModal: {
      ...customization.ExchangeBettingModal,
      preDefinedAmounts: preDefinedAmountsMapper({
        currencies: availableRegistrationCurrencies,
      }),
    },
  },
};

export default config;
